import { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useLanguage } from '@/contexts/LanguageContext';
import { getVotifierGuidePath, getAPIDocsPath } from '@/utils/i18nRouting';

export const LanguageSyncManager = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { language, setLanguage } = useLanguage();

    // Auto-redirect par langue navigateur (1ère visite uniquement)
    // Objectif : booster l'international via les domaines connectés (rankserveur.com / rankserver.online)
    useEffect(() => {
      try {
        const path = location.pathname;
        const isRoot = path === '/' || path === '';
        if (!isRoot) return;

        const STORAGE_KEY = 'rs_lang_autoredirect_done';
        if (typeof window === 'undefined') return;
        if (localStorage.getItem(STORAGE_KEY)) return;
        // Respecter un choix manuel déjà stocké
        if (localStorage.getItem('preferredLanguage') || localStorage.getItem('language')) {
          localStorage.setItem(STORAGE_KEY, '1');
          return;
        }

        const nav = (navigator.language || (navigator.languages && navigator.languages[0]) || '').toLowerCase();
        let target: 'en' | 'es' | null = null;
        if (nav.startsWith('es')) target = 'es';
        else if (nav && !nav.startsWith('fr')) target = 'en'; // tout non-FR → EN

        localStorage.setItem(STORAGE_KEY, '1');
        if (target) {
          navigate(`/${target}`, { replace: true });
        }
      } catch {
        // silencieux
      }
      // Une seule exécution au mount root
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
      const detectLanguageFromPath = () => {
        const path = location.pathname;

        // Nettoyer le path de tous les préfixes de langue empilés
        const cleanPath = path.replace(/^\/?((?:fr|en|es)\/?)+(.*?)$/, '/$2').replace(/\/+/g, '/');
        
        // Détecter la langue depuis l'URL propre
        let urlLanguage: 'fr' | 'en' | 'es';

        // Vérifier le premier préfixe de langue valide seulement
        if (path.match(/^\/en(?:\/|$)/)) {
          urlLanguage = 'en';
        } else if (path.match(/^\/es(?:\/|$)/)) {
          urlLanguage = 'es'; 
        } else if (path.match(/^\/fr(?:\/|$)/)) {
          urlLanguage = 'fr';
        } else {
          // Routes sans préfixe de langue : déterminer la langue selon le pattern
          if (cleanPath.startsWith('/servidor/') || cleanPath.startsWith('/servidores-') || cleanPath.startsWith('/guia-votifier')) {
            urlLanguage = 'es';
          } else if (cleanPath.startsWith('/server/') || cleanPath.startsWith('/servers-') || cleanPath.startsWith('/votifier-guide')) {
            urlLanguage = 'en';
          } else if (cleanPath.startsWith('/serveur/') || cleanPath.startsWith('/guide-votifier')) {
            urlLanguage = 'fr';
          } else {
            // Route neutre (/, /game/*, /profile, etc.) → garder la langue actuelle
            return; // Ne pas changer la langue
          }
        }

        // Mettre à jour la langue si nécessaire et éviter les loops
        if (urlLanguage !== language) {
          //console.log('🔄 Language sync update:', { from: language, to: urlLanguage, path });
          setLanguage(urlLanguage);
          
          // Rediriger vers l'équivalent dans la nouvelle langue si nécessaire
          const currentPath = cleanPath;
          let newPath = '';
          
          // Redirection pour les guides
          if (currentPath === '/guide-votifier' || currentPath === '/votifier-guide' || currentPath === '/guia-votifier') {
            newPath = getVotifierGuidePath(urlLanguage);
          } else if (currentPath === '/api-docs') {
            newPath = getAPIDocsPath(urlLanguage);
          }
          
          if (newPath && newPath !== path) {
            navigate(newPath, { replace: true });
          }
        }
      };

      detectLanguageFromPath();
    }, [location.pathname, language, setLanguage, navigate]);

  return null;
};